import { useMemo, useState } from 'react';
import { Button, Card, Empty, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, Typography } from 'antd';
import {
  DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DeleteOutlined, EditOutlined, MenuOutlined, PlusOutlined, RightOutlined, DownOutlined } from '@ant-design/icons';
import type { FormColumn, FormLayout, FormNode } from '@api/importApi';
import type { MetaAttributeDto } from '@/types/metadata';
import {
  type NodePath, pathKey, getNodeAtPath, updateNodeAtPath, deleteNodeAtPath,
  reorderAtParent, addChildAtPath, moveNode, pathToDndId, dndIdToPath, isContainerType,
} from './layoutTreeHelpers';

const { Text } = Typography;

interface Props {
  acqLayout: FormLayout | null;
  acqAttributes: MetaAttributeDto[];
  acqOnChange: (acqLayout: FormLayout) => void;
}

const NODE_KIND_OPTIONS: { value: FormNode['type']; label: string; color: string }[] = [
  { value: 'group',    label: 'Група',     color: 'cyan' },
  { value: 'field',    label: 'Поле',      color: 'blue' },
  { value: 'checkbox', label: 'Чекбокс',   color: 'geekblue' },
  { value: 'label',    label: 'Лейбл',     color: 'default' },
  { value: 'pages',    label: 'Таби',      color: 'purple' },
  { value: 'page',     label: 'Таб',       color: 'magenta' },
  { value: 'table',    label: 'Таблиця',   color: 'orange' },
];

// Path helpers + immutable mutators виокремлено у `./layoutTreeHelpers.ts`
// (для unit-тестів через Vitest без рендеру React).

function defaultNode(acqType: FormNode['type']): FormNode {
  switch (acqType) {
    case 'group': return { type: 'group', title: 'Нова група', children: [] };
    case 'field': return { type: 'field', title: 'Поле', attributeCode: '' };
    case 'checkbox': return { type: 'checkbox', title: 'Чекбокс', attributeCode: '' };
    case 'label': return { type: 'label', title: 'Лейбл' };
    case 'pages': return { type: 'pages', children: [{ type: 'page', title: 'Таб 1', children: [] }] };
    case 'page': return { type: 'page', title: 'Новий таб', children: [] };
    case 'table': return { type: 'table', title: 'Таблиця', rowsPath: '', columns: [] };
  }
}

/**
 * Drag-drop редактор LayoutJson (MVP).
 * Підтримує:
 *   • reorder через @dnd-kit/sortable (top-level + у group/page)
 *   • cross-container drag-drop (перенос вузла з top-level у group і навпаки) — Sprint 18
 *   • вкладеність group/page/pages (через collapse)
 *   • property panel справа (title, attributeCode, width, readOnly, rowsPath/columns для table)
 *   • додавання/видалення вузлів
 *   • columns editor для table-вузла (окрема модалка з reorder колонок)
 */
export default function LayoutTreeEditor({ acqLayout, acqAttributes, acqOnChange }: Props) {
  const acqNodes = acqLayout?.nodes ?? [];
  const [acqSelectedPath, setAcqSelectedPath] = useState<NodePath | null>(null);
  const [acqExpanded, setAcqExpanded] = useState<Set<string>>(new Set());

  const acqHeaderAttrs = useMemo(
    () => acqAttributes.filter((a) => a.section === 'header'),
    [acqAttributes],
  );

  const acqUpdateLayout = (acqNewNodes: FormNode[]) => {
    acqOnChange({ ...(acqLayout ?? {}), nodes: acqNewNodes });
  };

  const acqHandleNodeUpdate = (acqPath: NodePath, acqMutator: (n: FormNode) => FormNode) => {
    acqUpdateLayout(updateNodeAtPath(acqNodes, acqPath, acqMutator));
  };

  const acqHandleDelete = (acqPath: NodePath) => {
    acqUpdateLayout(deleteNodeAtPath(acqNodes, acqPath));
    if (acqSelectedPath && pathKey(acqSelectedPath) === pathKey(acqPath)) {
      setAcqSelectedPath(null);
    }
  };

  const acqHandleAddChild = (acqParentPath: NodePath, acqType: FormNode['type']) => {
    acqUpdateLayout(addChildAtPath(acqNodes, acqParentPath.indices, defaultNode(acqType)));
  };

  const acqHandleAddTopLevel = (acqType: FormNode['type']) => {
    acqUpdateLayout([...acqNodes, defaultNode(acqType)]);
  };

  const acqToggleExpand = (acqPath: NodePath) => {
    const acqKey = pathKey(acqPath);
    const acqNew = new Set(acqExpanded);
    if (acqNew.has(acqKey)) acqNew.delete(acqKey); else acqNew.add(acqKey);
    setAcqExpanded(acqNew);
  };

  const acqSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const acqHandleDragEnd = (acqEv: DragEndEvent) => {
    const { active, over } = acqEv;
    if (!over || active.id === over.id) return;
    const acqSrc = dndIdToPath(active.id as string);
    const acqDst = dndIdToPath(over.id as string);
    if (!acqSrc || acqSrc.length === 0 || !acqDst || acqDst.length === 0) return;

    const acqSrcParent = acqSrc.slice(0, -1);
    const acqDstParent = acqDst.slice(0, -1);
    const acqDstIdx = acqDst[acqDst.length - 1];

    // Same parent → reorder
    if (acqSrcParent.length === acqDstParent.length
        && acqSrcParent.every((v, i) => v === acqDstParent[i])) {
      acqUpdateLayout(reorderAtParent(acqNodes, acqSrcParent, acqSrc[acqSrc.length - 1], acqDstIdx));
      return;
    }
    // Cross-container → moveNode
    const acqMoved = moveNode(acqNodes, acqSrc, acqDstParent, acqDstIdx);
    if (acqMoved) acqUpdateLayout(acqMoved);
  };

  const acqSelectedNode = acqSelectedPath ? getNodeAtPath(acqNodes, acqSelectedPath) : null;

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <Space style={{ marginBottom: 8 }} wrap>
          <Text strong>Додати top-level:</Text>
          {NODE_KIND_OPTIONS.map((acqK) => (
            <Button key={acqK.value} size="small" icon={<PlusOutlined />} onClick={() => acqHandleAddTopLevel(acqK.value)}>
              {acqK.label}
            </Button>
          ))}
        </Space>

        {acqNodes.length === 0 ? (
          <Empty description="Layout порожній — додайте вузол" />
        ) : (
          <DndContext sensors={acqSensors} collisionDetection={closestCenter} onDragEnd={acqHandleDragEnd}>
            <SortableTree
              acqNodes={acqNodes}
              acqParentPath={[]}
              acqSelectedKey={acqSelectedPath ? pathKey(acqSelectedPath) : null}
              acqExpanded={acqExpanded}
              acqOnSelect={(p) => setAcqSelectedPath(p)}
              acqOnDelete={acqHandleDelete}
              acqOnAddChild={acqHandleAddChild}
              acqToggleExpand={acqToggleExpand}
            />
          </DndContext>
        )}
      </div>

      <Card size="small" title="Властивості вузла" style={{ flex: '0 0 320px', position: 'sticky', top: 8 }}>
        {!acqSelectedNode || !acqSelectedPath ? (
          <Text type="secondary">Оберіть вузол зліва щоб редагувати властивості</Text>
        ) : (
          <NodePropertyPanel
            acqNode={acqSelectedNode}
            acqPath={acqSelectedPath}
            acqRoot={acqNodes}
            acqHeaderAttrs={acqHeaderAttrs}
            acqOnChange={(m) => acqHandleNodeUpdate(acqSelectedPath, m)}
            acqOnMove={(acqToParentPath) => {
              const acqMoved = moveNode(acqNodes, acqSelectedPath.indices, acqToParentPath, Number.MAX_SAFE_INTEGER);
              if (acqMoved) {
                acqUpdateLayout(acqMoved);
                setAcqSelectedPath(null);
              }
            }}
          />
        )}
      </Card>
    </div>
  );
}

// ─────────────────────── SORTABLE TREE ───────────────────────
// Без власного DndContext — використовує єдиний DndContext з LayoutTreeEditor
// для cross-container drag-drop.

function SortableTree({
  acqNodes, acqParentPath, acqSelectedKey, acqExpanded,
  acqOnSelect, acqOnDelete, acqOnAddChild, acqToggleExpand,
}: {
  acqNodes: FormNode[];
  acqParentPath: number[];
  acqSelectedKey: string | null;
  acqExpanded: Set<string>;
  acqOnSelect: (p: NodePath) => void;
  acqOnDelete: (p: NodePath) => void;
  acqOnAddChild: (p: NodePath, t: FormNode['type']) => void;
  acqToggleExpand: (p: NodePath) => void;
}) {
  const acqIds = acqNodes.map((_, i) => pathToDndId([...acqParentPath, i]));

  return (
    <SortableContext items={acqIds} strategy={verticalListSortingStrategy}>
      <div>
        {acqNodes.map((acqN, i) => {
          const acqPath: NodePath = { indices: [...acqParentPath, i] };
          const acqKey = pathKey(acqPath);
          const acqIsSelected = acqSelectedKey === acqKey;
          const acqIsExpandable = isContainerType(acqN.type);
          const acqIsExpanded = acqExpanded.has(acqKey);
          return (
            <SortableNode
              key={pathToDndId(acqPath.indices)}
              acqId={pathToDndId(acqPath.indices)}
              acqNode={acqN}
              acqPath={acqPath}
              acqIsSelected={acqIsSelected}
              acqIsExpandable={acqIsExpandable}
              acqIsExpanded={acqIsExpanded}
              acqOnSelect={() => acqOnSelect(acqPath)}
              acqOnDelete={() => acqOnDelete(acqPath)}
              acqOnAddChild={(t) => acqOnAddChild(acqPath, t)}
              acqOnToggleExpand={() => acqToggleExpand(acqPath)}
            >
              {acqIsExpandable && acqIsExpanded && (
                <div style={{ paddingLeft: 24, marginTop: 4 }}>
                  {(acqN.children ?? []).length === 0 ? (
                    <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                      (порожньо — перетягніть вузол сюди або додайте через "+ дочірній")
                    </Text>
                  ) : null}
                  <SortableTree
                    acqNodes={acqN.children ?? []}
                    acqParentPath={acqPath.indices}
                    acqSelectedKey={acqSelectedKey}
                    acqExpanded={acqExpanded}
                    acqOnSelect={acqOnSelect}
                    acqOnDelete={acqOnDelete}
                    acqOnAddChild={acqOnAddChild}
                    acqToggleExpand={acqToggleExpand}
                  />
                </div>
              )}
            </SortableNode>
          );
        })}
      </div>
    </SortableContext>
  );
}

function SortableNode({
  acqId, acqNode, acqIsSelected, acqIsExpandable, acqIsExpanded,
  acqOnSelect, acqOnDelete, acqOnAddChild, acqOnToggleExpand, children,
}: {
  acqId: string;
  acqNode: FormNode;
  acqPath: NodePath;
  acqIsSelected: boolean;
  acqIsExpandable: boolean;
  acqIsExpanded: boolean;
  acqOnSelect: () => void;
  acqOnDelete: () => void;
  acqOnAddChild: (t: FormNode['type']) => void;
  acqOnToggleExpand: () => void;
  children?: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: acqId });
  const acqStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    marginBottom: 4,
  };

  const acqKindOpt = NODE_KIND_OPTIONS.find((k) => k.value === acqNode.type);
  const acqLabel = acqNode.title ?? acqNode.attributeCode ?? acqNode.rowsPath ?? '(без назви)';

  return (
    <div ref={setNodeRef} style={acqStyle}>
      <div
        onClick={acqOnSelect}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
          background: acqIsSelected ? '#e6f4ff' : '#fafafa',
          border: `1px solid ${acqIsSelected ? '#1677ff' : '#eee'}`,
          borderRadius: 4,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span {...attributes} {...listeners} style={{ cursor: 'grab', color: '#999' }} onClick={(e) => e.stopPropagation()}>
          <MenuOutlined />
        </span>
        {acqIsExpandable && (
          <span onClick={(e) => { e.stopPropagation(); acqOnToggleExpand(); }} style={{ cursor: 'pointer', color: '#666', fontSize: 10 }}>
            {acqIsExpanded ? <DownOutlined /> : <RightOutlined />}
          </span>
        )}
        <Tag color={acqKindOpt?.color}>{acqKindOpt?.label ?? acqNode.type}</Tag>
        <Text>{acqLabel}</Text>
        {acqNode.attributeCode && <Text type="secondary" code style={{ fontSize: 10 }}>{acqNode.attributeCode}</Text>}
        <span style={{ flex: 1 }} />
        {acqIsExpandable && (
          <Select
            size="small"
            placeholder="+ дочірній…"
            style={{ width: 130 }}
            value={undefined}
            onClick={(e) => e.stopPropagation()}
            options={NODE_KIND_OPTIONS.map((k) => ({ value: k.value, label: k.label }))}
            onChange={(v: FormNode['type']) => acqOnAddChild(v)}
          />
        )}
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={(e) => { e.stopPropagation(); acqOnDelete(); }}
        />
      </div>
      {children}
    </div>
  );
}

// ─────────────────────── PROPERTY PANEL ───────────────────────

function NodePropertyPanel({
  acqNode, acqPath, acqRoot, acqHeaderAttrs, acqOnChange, acqOnMove,
}: {
  acqNode: FormNode;
  acqPath: NodePath;
  acqRoot: FormNode[];
  acqHeaderAttrs: MetaAttributeDto[];
  acqOnChange: (m: (n: FormNode) => FormNode) => void;
  acqOnMove: (acqToParentPath: number[]) => void;
}) {
  const acqAttrOptions = useMemo(
    () => acqHeaderAttrs.map((a) => ({ value: a.code, label: `${a.name} (${a.code})` })),
    [acqHeaderAttrs],
  );
  const acqTableSections = useMemo(() => {
    const acqSet = new Set<string>();
    for (const a of acqHeaderAttrs) {
      if (a.section && a.section !== 'header') acqSet.add(a.section);
    }
    return Array.from(acqSet).map((s) => ({ value: s, label: s }));
  }, [acqHeaderAttrs]);

  // MetaAttribute для рядків поточної табл. частини (для Columns editor)
  const acqRowAttrs = useMemo(() => {
    if (acqNode.type !== 'table' || !acqNode.rowsPath) return [];
    return acqHeaderAttrs.filter((a) => a.section === acqNode.rowsPath);
  }, [acqHeaderAttrs, acqNode]);

  // Список container-вузлів (group/page/pages) — для "Перемістити у…"
  // Виключаємо власний шлях і всіх нащадків (інакше створюємо цикл).
  const acqMoveTargets = useMemo(() => {
    const acqList: { value: string; label: string }[] = [
      { value: '__root__', label: '(top-level)' },
    ];
    const acqWalk = (acqList2: FormNode[], acqPathPrefix: number[], acqDepth: number) => {
      for (let i = 0; i < acqList2.length; i++) {
        const acqChildPath = [...acqPathPrefix, i];
        const acqChild = acqList2[i];
        // Заборонити перемістити у себе або у нащадка
        const acqIsSelfOrDescendant = acqChildPath.length >= acqPath.indices.length
          && acqPath.indices.every((v, j) => acqChildPath[j] === v);
        if (isContainerType(acqChild.type) && !acqIsSelfOrDescendant) {
          acqList.push({
            value: acqChildPath.join('-'),
            label: `${' '.repeat(acqDepth * 2)}↳ ${acqChild.title ?? acqChild.type}`,
          });
        }
        if (acqChild.children) acqWalk(acqChild.children, acqChildPath, acqDepth + 1);
      }
    };
    acqWalk(acqRoot, [], 0);
    return acqList;
  }, [acqRoot, acqPath]);

  const [acqColumnsModalOpen, setAcqColumnsModalOpen] = useState(false);

  const acqSet = (key: keyof FormNode, value: any) => acqOnChange((n) => ({ ...n, [key]: value }));

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      <div>
        <Text type="secondary" style={{ fontSize: 11 }}>Тип</Text>
        <div><Tag>{acqNode.type}</Tag></div>
      </div>

      <div>
        <Text type="secondary" style={{ fontSize: 11 }}>Заголовок</Text>
        <Input
          size="small"
          value={acqNode.title ?? ''}
          onChange={(e) => acqSet('title', e.target.value || undefined)}
        />
      </div>

      {(acqNode.type === 'field' || acqNode.type === 'checkbox') && (
        <div>
          <Text type="secondary" style={{ fontSize: 11 }}>attributeCode (поле шапки)</Text>
          <Select
            size="small"
            style={{ width: '100%' }}
            value={acqNode.attributeCode ?? undefined}
            onChange={(v) => acqSet('attributeCode', v)}
            options={acqAttrOptions}
            showSearch
            allowClear
            placeholder="Оберіть реквізит…"
            filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </div>
      )}

      {acqNode.type === 'table' && (
        <>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>rowsPath (Section табл. частини)</Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={acqNode.rowsPath ?? undefined}
              onChange={(v) => acqSet('rowsPath', v)}
              options={acqTableSections}
              showSearch
              allowClear
              placeholder="Оберіть section…"
            />
          </div>
          <div>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Колонки: {acqNode.columns?.length ?? 0}
              </Text>
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={!acqNode.rowsPath}
                onClick={() => setAcqColumnsModalOpen(true)}
              >
                Редагувати…
              </Button>
            </Space>
            {!acqNode.rowsPath && (
              <Text type="warning" style={{ fontSize: 11 }}>Спочатку оберіть rowsPath</Text>
            )}
          </div>
          <ColumnsEditorModal
            acqOpen={acqColumnsModalOpen}
            acqColumns={acqNode.columns ?? []}
            acqRowAttrs={acqRowAttrs}
            acqOnCancel={() => setAcqColumnsModalOpen(false)}
            acqOnSave={(acqCols) => { acqSet('columns', acqCols); setAcqColumnsModalOpen(false); }}
          />
        </>
      )}

      {acqNode.type === 'field' && (
        <div>
          <Text type="secondary" style={{ fontSize: 11 }}>Width (px / 12)</Text>
          <InputNumber
            size="small"
            style={{ width: '100%' }}
            value={acqNode.width ?? undefined}
            onChange={(v) => acqSet('width', v ?? undefined)}
            min={1} max={100}
          />
        </div>
      )}

      <div>
        <Space>
          <Text type="secondary" style={{ fontSize: 11 }}>Read-only</Text>
          <Switch
            size="small"
            checked={!!acqNode.readOnly}
            onChange={(v) => acqSet('readOnly', v || undefined)}
          />
        </Space>
      </div>

      <div>
        <Text type="secondary" style={{ fontSize: 11 }}>Перемістити у…</Text>
        <Select
          size="small"
          style={{ width: '100%' }}
          placeholder="Оберіть контейнер…"
          value={undefined}
          options={acqMoveTargets}
          onChange={(v: string) => {
            if (v === '__root__') acqOnMove([]);
            else acqOnMove(v.split('-').map((s) => parseInt(s, 10)));
          }}
        />
      </div>
    </Space>
  );
}

// ─────────────────────── COLUMNS EDITOR (для table) ───────────────────────

function ColumnsEditorModal({
  acqOpen, acqColumns, acqRowAttrs, acqOnCancel, acqOnSave,
}: {
  acqOpen: boolean;
  acqColumns: FormColumn[];
  acqRowAttrs: MetaAttributeDto[];
  acqOnCancel: () => void;
  acqOnSave: (acqCols: FormColumn[]) => void;
}) {
  const [acqDraft, setAcqDraft] = useState<FormColumn[]>(acqColumns);

  // Скидаємо draft коли модалка відкривається/закривається
  useMemo(() => { if (acqOpen) setAcqDraft(acqColumns); }, [acqOpen, acqColumns]);

  const acqAvailableAttrs = useMemo(
    () => acqRowAttrs.map((a) => ({ value: a.code, label: `${a.name} (${a.code})` })),
    [acqRowAttrs],
  );

  const acqSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const acqIds = acqDraft.map((_, i) => `col-${i}`);

  const acqHandleDragEnd = (acqEv: DragEndEvent) => {
    const { active, over } = acqEv;
    if (!over || active.id === over.id) return;
    const acqOld = acqIds.indexOf(active.id as string);
    const acqNew = acqIds.indexOf(over.id as string);
    if (acqOld < 0 || acqNew < 0) return;
    setAcqDraft(arrayMove(acqDraft, acqOld, acqNew));
  };

  const acqAddColumn = () => {
    setAcqDraft([...acqDraft, { rowFieldCode: '', title: '' }]);
  };

  const acqAddAllMissing = () => {
    const acqExisting = new Set(acqDraft.map((c) => c.rowFieldCode));
    const acqMissing = acqRowAttrs.filter((a) => !acqExisting.has(a.code));
    setAcqDraft([
      ...acqDraft,
      ...acqMissing.map((a) => ({ rowFieldCode: a.code, title: a.name })),
    ]);
  };

  const acqUpdateColumn = (acqIdx: number, acqPatch: Partial<FormColumn>) => {
    setAcqDraft(acqDraft.map((c, i) => i === acqIdx ? { ...c, ...acqPatch } : c));
  };

  const acqDeleteColumn = (acqIdx: number) => {
    setAcqDraft(acqDraft.filter((_, i) => i !== acqIdx));
  };

  return (
    <Modal
      open={acqOpen}
      title="Колонки таблиці"
      onCancel={acqOnCancel}
      onOk={() => acqOnSave(acqDraft)}
      okText="Зберегти"
      cancelText="Скасувати"
      width={780}
      destroyOnClose
    >
      <Space style={{ marginBottom: 8 }}>
        <Button size="small" icon={<PlusOutlined />} onClick={acqAddColumn}>Порожня колонка</Button>
        <Button size="small" onClick={acqAddAllMissing} disabled={acqRowAttrs.length === 0}>
          + усі відсутні реквізити
        </Button>
        <Text type="secondary" style={{ fontSize: 11 }}>Перетягуйте за ⠿ для зміни порядку</Text>
      </Space>
      {acqDraft.length === 0 ? (
        <Empty description="Немає колонок — додайте через кнопки вище" />
      ) : (
        <DndContext sensors={acqSensors} collisionDetection={closestCenter} onDragEnd={acqHandleDragEnd}>
          <SortableContext items={acqIds} strategy={verticalListSortingStrategy}>
            <Table
              dataSource={acqDraft.map((c, i) => ({ ...c, _idx: i, key: i }))}
              pagination={false}
              size="small"
              components={{ body: { row: SortableRow } }}
              onRow={(record) => ({ 'data-row-id': `col-${record._idx}` } as React.HTMLAttributes<HTMLTableRowElement>)}
              columns={[
                {
                  title: '', dataIndex: '_drag', width: 32, render: () => (
                    <span style={{ cursor: 'grab', color: '#999' }}>⠿</span>
                  ),
                },
                {
                  title: 'rowFieldCode (реквізит)', dataIndex: 'rowFieldCode', width: 280,
                  render: (_v: string, _r: any, idx: number) => (
                    <Select
                      size="small"
                      style={{ width: '100%' }}
                      value={acqDraft[idx].rowFieldCode || undefined}
                      onChange={(v) => acqUpdateColumn(idx, { rowFieldCode: v })}
                      options={acqAvailableAttrs}
                      showSearch
                      placeholder="Оберіть реквізит…"
                      filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    />
                  ),
                },
                {
                  title: 'Заголовок колонки', dataIndex: 'title',
                  render: (_v: string, _r: any, idx: number) => (
                    <Input
                      size="small"
                      value={acqDraft[idx].title ?? ''}
                      onChange={(e) => acqUpdateColumn(idx, { title: e.target.value || undefined })}
                      placeholder="(дефолт — назва реквізиту)"
                    />
                  ),
                },
                {
                  title: '', dataIndex: '_actions', width: 50,
                  render: (_: unknown, _r: any, idx: number) => (
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => acqDeleteColumn(idx)} />
                  ),
                },
              ]}
            />
          </SortableContext>
        </DndContext>
      )}
    </Modal>
  );
}

function SortableRow(props: React.HTMLAttributes<HTMLTableRowElement> & { 'data-row-id'?: string }) {
  const acqId = props['data-row-id'];
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: acqId ?? '_unused' });
  const acqStyle: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  if (!acqId) return <tr {...props} />;
  return <tr ref={setNodeRef} {...props} {...attributes} {...listeners} style={acqStyle} />;
}
